import { activityFetch as fetch } from './feedback/requestActivity'
import { useEffect, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import styled from "styled-components"
import {
  Activity, BookOpen, Box, Boxes, Cloud, Cpu, CreditCard, Database,
  HeadphonesIcon, LayoutDashboard, MonitorCog, MonitorPlay, Network,
  Server, Users,
} from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { featureGuideForPath } from "../screen/Guide/featureGuides"

const pages = {
  "/": ["내 자산", LayoutDashboard],
  "/operations": ["서버 서비스", MonitorCog],
  "/software": ["소프트웨어 출시", Box],
  "/monitoring": ["운영 모니터링", Activity],
  "/infrastructure": ["인프라 현황", Server],
  "/ai-services": ["AI 설계", Cpu],
  "/compute": ["컴퓨트", Server],
  "/storage": ["클라우드 스토리지", Cloud],
  "/kubernetes": ["오케스트레이션", Boxes],
  "/serverless": ["서버리스", Box],
  "/registry": ["컨테이너 레지스트리", Box],
  "/containers": ["컨테이너 운영 이력", Boxes],
  "/labs": ["데이터레이크 설계", Database],
  "/management-plan": ["관리계획 가이드", BookOpen],
  "/deploy": ["서버 배포", Server],
  "/deploy/plan": ["배포 설계", MonitorCog],
  "/monitor": ["배포 모니터링", Activity],
  "/database": ["데이터베이스", Database],
  "/network": ["네트워크", Network],
  "/rtremote": ["원격 접속", MonitorPlay],
  "/account": ["계정 관리", Users],
  "/support": ["고객 지원", HeadphonesIcon],
  "/referral": ["추천", Users],
  "/billing": ["결제", CreditCard],
  "/guide": ["가이드", BookOpen],
  "/event": ["이벤트", Activity],
  "/projectpage": ["프로젝트", Boxes],
  "/projectform": ["프로젝트 등록", Boxes],
}

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: .8rem;
  min-height: 52px;
  flex-shrink: 0;
  padding: 0 var(--page-space);
  border-bottom: 1px solid #e5eaf1;
  background: linear-gradient(110deg, #f0f5ff, #fff 65%);
  color: #172437;
  .context-menu,.context-mobile-actions,.context-brand { display:none; }
  @media (max-width: 599px) {
    min-height:60px; padding:6px 10px; box-sizing:border-box; gap:0 8px;
    display:grid; grid-template-columns:44px minmax(0,1fr) auto auto; grid-template-rows:1fr 1fr;
    .context-menu{display:block;grid-column:1;grid-row:1/3;}
    .context-mobile-actions{display:flex;align-items:center;grid-column:4;grid-row:1/3;gap:2px;}
    .context-mobile-actions a:last-child{background:var(--accent-soft);color:var(--accent);}
    .context-brand{display:inline;color:#60728d;font-weight:600;margin-right:4px;}
  }
  @media (max-width:359px) {.context-brand{display:none;}gap:0 5px;}
`
const IconTile = styled.div`
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  color: #8494ab;
  @media (max-width:599px) { display:none; }
`
const Heading = styled.div`
  min-width: 0;
  strong { display: block; font-size: 13px; font-weight: 500; line-height: 1.3; color: #435a70; }
  small { display: block; color: #607086; font-size: .74rem; }
  @media (max-width:599px) { grid-column:2;grid-row:1;align-self:end;strong {font-size:16px;font-weight:600;color:#172b43;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;} }
`
const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  margin-left: auto;
  padding: 6px 9px;
  border: 0;
  border-radius: 7px;
  background: ${p => p.$tone === "ok" ? "#eaf6ee" : p.$tone === "error" ? "#fff0f2" : "#eef2f8"};
  color: ${p => p.$tone === "ok" ? "#17633d" : p.$tone === "error" ? "#a52d2d" : "#526176"};
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  @media (max-width:599px) {grid-column:2;grid-row:2;align-self:start;margin:3px 0 0;padding:0;background:transparent;font-size:10px;gap:3px;}
`
const HelpLink = styled(Link)`
  display:inline-flex; align-items:center; gap:.35rem; padding:.4rem .55rem;
  border-radius:5px; color:#63758b; text-decoration:none; font-size:12px; font-weight:500;
  &:hover{background:#e9f1ff;} &:focus-visible{outline:2px solid #2563eb;outline-offset:2px;}
  @media (max-width:839px) { padding:0; width:32px; min-height:44px; justify-content:center; flex-shrink:0; span{display:none;} }
  @media (max-width:599px) {grid-column:3;grid-row:1/3;}
`
const Lamp = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${p => p.$tone === "ok" ? "#229e58" : p.$tone === "error" ? "#d94343" : "#8793a3"};
`

const publicPaths = new Set(["/login", "/signup", "/forgot-password", "/protocol-handler"])

export function PageContextBar({menuControl,mobileActions}) {
  const { pathname } = useLocation()
  const { isLoggedIn } = useAuth() || {}
  const [api, setApi] = useState("unknown")
  const normalizedPath = pathname.toLowerCase()
  const [title, Icon] = pages[normalizedPath] || ["스카이넷 화면", LayoutDashboard]
  const guide = featureGuideForPath(normalizedPath)

  useEffect(() => {
    if (!isLoggedIn || publicPaths.has(normalizedPath)) {
      setApi("unknown")
      return undefined
    }
    let active = true
    let controller
    const probe = async () => {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token")
      if (!token) { if (active) setApi("unknown"); return }
      controller?.abort()
      controller = new AbortController()
      const currentController = controller
      const timeout = window.setTimeout(() => currentController.abort(), 8000)
      if (active) setApi("checking")
      try {
        const response = await fetch("/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
          signal: currentController.signal,
        })
        if (!active) return
        if (response.status === 401 || response.status === 403) { setApi("auth"); return }
        if (!response.ok) { setApi(response.status >= 500 ? "error" : "unknown"); return }
        const data = await response.json()
        setApi(data?.success && data?.user ? "ok" : "unknown")
      } catch (error) {
        if (active && (error.name !== "AbortError" || currentController === controller)) setApi("unknown")
      } finally {
        window.clearTimeout(timeout)
      }
    }
    probe()
    const interval = window.setInterval(probe, 60000)
    const onVisible = () => { if (document.visibilityState === "visible") probe() }
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      active = false
      controller?.abort()
      window.clearInterval(interval)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [isLoggedIn, normalizedPath])

  if (publicPaths.has(normalizedPath)) return null
  const tone = api === "ok" ? "ok" : api === "error" ? "error" : "unknown"
  const label = api === "ok" ? "관리 API 연결" : api === "error" ? "관리 API 오류" :
    api === "auth" ? "로그인 재확인 필요" : api === "checking" ? "관리 API 확인 중" : "관리 API 확인 불가"
  return (
    <Bar aria-label="현재 화면과 관리 API 상태">
      <div className="context-menu">{menuControl}</div>
      <IconTile aria-hidden="true"><Icon size={16} /></IconTile>
      <Heading><strong title={title}>{title}</strong></Heading>
      {guide && <HelpLink to={`/guide?feature=${guide.key}`} aria-label="이 화면 가이드" title="이 화면 가이드"><BookOpen size={15} aria-hidden="true" /><span>이 화면 가이드</span></HelpLink>}
      <Badge $tone={tone} role="status" aria-label={`관리 API 상태: ${label}`}>
        <span className="context-brand">Skynet ·</span><Lamp $tone={tone} aria-hidden="true" />{label}
      </Badge>
      <div className="context-mobile-actions">{mobileActions}</div>
    </Bar>
  )
}
